import { useMemo, useState } from "react";
import {
  Stack,
  Group,
  Text,
  ActionIcon,
  ScrollArea,
  TextInput,
  UnstyledButton,
} from "@mantine/core";
import { IconCheck, IconSearch, IconX } from "@tabler/icons-react";
import styles from "../MobileAppointmentForm.module.css";
import { calculateTotalDuration } from "../../../../../../utils/timeUtils";
import { formatCurrency } from "../../../../../../utils/cashDrawerUtils";

function ServiceSection({
  formData,
  onChange,
  onClose,
  services,
  errors,
  validateSection,
  staffName,
}) {
  const [query, setQuery] = useState("");

  // Client-side filter — the full service list already lives in SalonContext,
  // so there is no fetch here. Tokens are AND-ed so "hydra facial" still
  // matches "Hydra Glow Facial".
  const filteredServices = useMemo(() => {
    const tokens = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (!tokens.length) return services;

    return services.filter((service) => {
      const haystack = (service.name ?? "").toLowerCase();
      return tokens.every((token) => haystack.includes(token));
    });
  }, [services, query]);

  const handleServiceSelect = (serviceId) => {
    const service = services.find((s) => s.id === serviceId);
    onChange({
      salonServiceId: serviceId,
      baseDuration: service.default_base_duration_mins ?? 30,
      processingTime: service.default_processing_time_mins ?? 0,
      finishingTime: service.default_finishing_time_mins ?? 0,
      bufferTime: service.default_buffer_time_mins ?? 0,
    });
  };

  const handleClose = () => {
    validateSection("service");
    onClose();
  };

  const formatDuration = (service) => {
    const minutes = calculateTotalDuration(service);
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    const separator = hours && mins ? " " : "";
    return `${hours ? `${hours}hr` : ""}${separator}${mins ? `${mins}min` : ""}`;
  };

  return (
    <Stack gap={0} p="md" h="100dvh">
      {/* Header */}
      <Group w="100%" justify="space-between">
        <Text size="lg" fw={500}>
          Select Service
        </Text>
        <ActionIcon
          onClick={handleClose}
          variant="subtle"
          color="var(--mantine-color-teal-1)"
          aria-label="Done selecting service"
        >
          <IconCheck size={20} color="var(--mantine-color-teal-6)" />
        </ActionIcon>
      </Group>

      {/* Error Message */}
      {errors.salonServiceId && (
        <Text size="sm" c="red">
          {errors.salonServiceId}
        </Text>
      )}

      {/* Search */}
      <TextInput
        mt="sm"
        type="search"
        autoCapitalize="none"
        autoCorrect="off"
        placeholder="Search services"
        aria-label="Search services"
        value={query}
        onChange={(event) => setQuery(event.currentTarget.value)}
        leftSection={<IconSearch size={16} />}
        rightSection={
          query ? (
            <ActionIcon
              variant="subtle"
              color="gray"
              aria-label="Clear search"
              onClick={() => setQuery("")}
            >
              <IconX size={16} />
            </ActionIcon>
          ) : null
        }
      />

      {/* Service List */}
      <ScrollArea style={{ flex: 1, minHeight: 0 }} mt="md">
        {filteredServices.length === 0 ? (
          <Text size="sm" c="dimmed" ta="center" mt="xl">
            {query.trim() ? (
              <>
                No services match &ldquo;{query.trim()}&rdquo;
                {staffName ? ` for ${staffName}` : ""}
              </>
            ) : (
              <>No services assigned{staffName ? ` to ${staffName}` : ""}</>
            )}
          </Text>
        ) : (
          <Stack gap={0}>
            {filteredServices.map((service) => (
              <UnstyledButton
                key={service.id}
                w="100%"
                className={`${styles.selectionItem} ${formData.salonServiceId === service.id ? styles.selected : ""}`}
                onClick={() => handleServiceSelect(service.id)}
              >
                <Stack gap={0}>
                  <Text size="md" fw={500}>
                    {service.name}
                  </Text>
                  <Group gap="xs">
                    <Text size="sm" c="dimmed">
                      {formatCurrency(service.default_price)}
                    </Text>
                    <Text size="sm" c="dimmed">
                      •
                    </Text>
                    <Text size="sm" c="dimmed">
                      {formatDuration(service)}
                    </Text>
                  </Group>
                </Stack>
              </UnstyledButton>
            ))}
          </Stack>
        )}
      </ScrollArea>
    </Stack>
  );
}

export default ServiceSection;
